import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { ApiResponse, PaginatedData, Subscription } from '@/types';

// Check if a page is subscribed
export function useIsSubscribed(slug: string, isAuthenticated: boolean = true) {
  return useQuery({
    queryKey: ['subscription', slug],
    queryFn: async (): Promise<boolean> => {
      const response = await api.get<ApiResponse<{ subscribed: boolean }>>(`/pages/${slug}/subscribed`);
      if (!response.data.success || !response.data.data) {
        return false;
      }
      return response.data.data.subscribed;
    },
    enabled: !!slug && isAuthenticated,
  });
}

// Get user's subscriptions list
export function useSubscriptions(page: number = 1, perPage: number = 20) {
  return useQuery({
    queryKey: ['subscriptions', page, perPage],
    queryFn: async (): Promise<PaginatedData<Subscription>> => {
      const response = await api.get<ApiResponse<PaginatedData<Subscription>>>(
        `/users/me/subscriptions?page=${page}&perPage=${perPage}`
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch subscriptions');
      }
      return response.data.data;
    },
  });
}

// Subscribe to page
export function useSubscribe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string) => {
      const response = await api.post<ApiResponse<Subscription>>(`/pages/${slug}/subscribe`);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to subscribe');
      }
      return response.data.data;
    },
    onSuccess: (_, slug) => {
      queryClient.setQueryData(['subscription', slug], true);
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });
}

// Unsubscribe from page
export function useUnsubscribe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string) => {
      const response = await api.delete<ApiResponse>(`/pages/${slug}/subscribe`);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'Failed to unsubscribe');
      }
      return response.data.data;
    },
    onSuccess: (_, slug) => {
      queryClient.setQueryData(['subscription', slug], false);
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });
}

// Toggle subscription
export function useToggleSubscription() {
  const subscribe = useSubscribe();
  const unsubscribe = useUnsubscribe();

  return {
    mutate: (slug: string, isSubscribed: boolean) => {
      if (isSubscribed) {
        unsubscribe.mutate(slug);
      } else {
        subscribe.mutate(slug);
      }
    },
    isPending: subscribe.isPending || unsubscribe.isPending,
  };
}
